/**
 * Component: GuidelineSectionUsageChart
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R07
 *
 * Time-series chart component displaying guideline sections viewed over time.
 * ADMIN-V1-R07: System shall provide a usage dashboard displaying guideline
 *               sections viewed over time.
 * Accessibility: WCAG 2.1 AA compliant with proper ARIA labels
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { format, parseISO } from 'date-fns';

/**
 * Data point for guideline section usage over time
 */
export interface UsageDataPoint {
  /** ISO 8601 timestamp */
  timestamp: string;
  /** Number of views for this time period */
  views: number;
  /** Optional: unique sections viewed */
  uniqueSections?: number;
}

export interface GuidelineSectionUsageChartProps {
  /** Array of usage data points ordered by timestamp */
  data: UsageDataPoint[];
  /** Chart height in pixels (default: 300) */
  height?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Date format for X-axis labels (default: 'MMM d') */
  dateFormat?: string;
  /** Accessible description for screen readers */
  description?: string;
}

/**
 * Custom tooltip component for the chart
 */
interface TooltipPayload {
  timestamp: string;
  views: number;
  uniqueSections?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TooltipPayload }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps): React.ReactElement | null {
  if (!active || !payload?.[0]) {
    return null;
  }

  const data = payload[0].payload;
  const date = parseISO(data.timestamp);

  return (
    <div
      className="rounded-md border bg-background p-3 shadow-md"
      role="tooltip"
    >
      <p className="text-sm font-medium">
        {format(date, 'MMMM d, yyyy')}
      </p>
      <p className="text-sm text-muted-foreground">
        Views: <span className="font-medium text-foreground">{data.views.toLocaleString()}</span>
      </p>
      {data.uniqueSections !== undefined && (
        <p className="text-sm text-muted-foreground">
          Unique sections: <span className="font-medium text-foreground">{data.uniqueSections.toLocaleString()}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton({ height }: { height: number }): React.ReactElement {
  return (
    <div
      className="animate-pulse rounded-md bg-muted"
      style={{ height }}
      aria-label="Loading chart"
      role="progressbar"
    />
  );
}

/**
 * Empty state when no data is available
 */
function EmptyState(): React.ReactElement {
  return (
    <div
      className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-dashed"
      role="status"
    >
      <p className="text-sm text-muted-foreground">
        No usage data available for the selected period
      </p>
    </div>
  );
}

/**
 * Time-series area chart displaying guideline section views over time.
 *
 * ADMIN-V1-R07: Provides visual representation of guideline sections
 * viewed over time for the admin usage dashboard.
 */
export function GuidelineSectionUsageChart({
  data,
  height = 300,
  isLoading = false,
  dateFormat = 'MMM d',
  description = 'Chart showing guideline section views over time',
}: GuidelineSectionUsageChartProps): React.ReactElement {
  // Format data for chart consumption
  const chartData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      date: format(parseISO(point.timestamp), dateFormat),
    }));
  }, [data, dateFormat]);

  // Calculate Y-axis domain for consistent scaling
  const yDomain = useMemo(() => {
    if (data.length === 0) return [0, 100];
    const maxViews = Math.max(...data.map((d) => d.views));
    // Add 10% padding to max value, minimum of 10
    return [0, Math.max(10, Math.ceil(maxViews * 1.1))];
  }, [data]);

  if (isLoading) {
    return <ChartSkeleton height={height} />;
  }

  if (data.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      role="img"
      aria-label={description}
      className="w-full"
    >
      {/* Screen reader description */}
      <span className="sr-only">
        {description}.
        {data.length > 0 && data[0] && (
          <> Data ranges from {format(parseISO(data[0].timestamp), 'MMMM d, yyyy')}</>
        )}
        {data.length > 0 && (() => {
          const lastItem = data[data.length - 1];
          return lastItem ? <> to {format(parseISO(lastItem.timestamp), 'MMMM d, yyyy')}</> : null;
        })()}
        . Total data points: {data.length}.
      </span>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            className="stroke-muted"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            className="text-muted-foreground"
            dy={10}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            className="text-muted-foreground"
            width={50}
            tickFormatter={(value: number) => value.toLocaleString()}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="views"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#viewsGradient)"
            name="Section Views"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default GuidelineSectionUsageChart;
